import { useCallback, useEffect, useState } from 'react';
import type { RootState } from 'store';
import Lottie from 'react-lottie-player';
import Button from '@/components/Button/Button';
import useViewport from '@/common/context/ViewPort/useViewPort';
import { Text } from '@/components/Text/Text';

import './EmptyCart.scss';
import { useGetDoneDesignsQuery } from '@/api/DesignQuery';
import { openExternalLink } from '@/components/utils/FunctionUtils';
import { DESIGN_TOOL } from '@/constants/Config';
import { ACCOUNT_TYPES } from '@/constants/Home';
import { STORE_CART_EMPTY_CTA_LABEL } from '@/modules/Cart/StoreCart/storeCart.constants';
import { captureEmptyCartCtaClicked } from '@/mixpanel/MixpanelEvents';
import { useAppSelector } from '@/store/hooks';
import { applyCorporateDesignToolParamIfNeeded } from '@/utilities/DesignToolUrlUtils';

interface IEmptyCartProps {
  handleAddItemsToCart: () => void;
  isStoreCart?: boolean;
}

const EmptyCart = ({ handleAddItemsToCart, isStoreCart = false }: IEmptyCartProps) => {
  const screen = useViewport();
  const styleClass = `empty-cart-section--${screen}`;
  const [completedDesigns, setCompletedDesigns] = useState(false);
  const { user } = useAppSelector((state: RootState) => state.user);
  const isCorporateAccount = user?.accountType === ACCOUNT_TYPES.CORPORATE;

  const { data } = useGetDoneDesignsQuery({
    limit: 1,
    page: 0,
    fetchOnlyDone: true,
  });

  /**
   * This hook hepls us to set the completed designs details.
   */
  useEffect(() => {
    if (data) {
      setCompletedDesigns(true);
    } else {
      setCompletedDesigns(false);
    }
  }, [data]);

  const rootClassName = [isStoreCart ? 'empty-cart-section--store' : ''].filter(Boolean).join(' ');

  const handleAddItemsClick = useCallback((): void => {
    if (!isStoreCart) {
      captureEmptyCartCtaClicked('Add Items');
    }
    handleAddItemsToCart();
  }, [handleAddItemsToCart, isStoreCart]);

  const handleStartDesigningClick = useCallback((): void => {
    if (!isStoreCart) {
      captureEmptyCartCtaClicked('Start Designing');
    }
    openExternalLink(applyCorporateDesignToolParamIfNeeded(DESIGN_TOOL, isCorporateAccount));
  }, [isCorporateAccount, isStoreCart]);

  return (
    <div className={rootClassName || undefined}>
      <div className={`${styleClass}__layout`}>
        <div className={`${styleClass}__label ${!completedDesigns ? 'no-done-design-label' : ' '}`}>
          {completedDesigns ? (
            <Text>Your cart is empty and desperate for attention.</Text>
          ) : (
            <Text>Looks like you haven’t started designing yet!</Text>
          )}

          {!completedDesigns && (
            <div className={`${styleClass}__label__no-done-designs`}>
              <Text>
                Once you have a design, you’ll be able to add it to the cart and checkout.
              </Text>
            </div>
          )}
        </div>
        <div className={`${styleClass}__body`}>
          <div className="empty-cart-ghost">
            <Lottie
              path="/src/assets/animations/ghost_animation.json"
              loop
              play
              style={{ width: 177, height: 177 }}
            />
          </div>
          <div className="empty-cart-image">
            <img src="/src/assets/svg/empty_cart_thin.svg" alt="empty-cart" />
          </div>
          <div className={`details-cart--${screen} ${!completedDesigns ? 'no-done-designs' : ''}`}>
            {completedDesigns ? (
              <Button
                className={isStoreCart ? 'store-empty-cart-btn' : 'left-icon-cart-btn'}
                iconClassName="btn-cart-icon"
                iconOnLeftSrc="/src/assets/svg/s_cart_white.svg"
                labelClassName="btn-cart-label"
                onClick={handleAddItemsClick}
              >
                {isStoreCart ? STORE_CART_EMPTY_CTA_LABEL : 'Add Items to Cart'}
              </Button>
            ) : (
              <Button
                className="cart-start-design-btn"
                iconClassName="btn-icon"
                labelClassName="btn-label"
                iconOnRightSrc="/src/assets/svg/right_arrow_green.svg"
                onClick={handleStartDesigningClick}
              >
                Start Designing
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

EmptyCart.displayName = 'EmptyCart';

export default EmptyCart;
